import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import ActionPanel from './components/ActionPanel/ActionPanel';
import BreadcrumbSection from './components/BreadcrumbSection/BreadcrumbSection';
import CompetitorsTab from './components/CompetitorsTab/CompetitorsTab';
import OverviewTab from './components/OverviewTab/OverviewTab';
import PageTabs from './components/PageTabs/PageTabs';
import PricingTab from './components/PricingTab/PricingTab';
import ProductAnalysisSkeleton from './components/ProductAnalysisSkeleton/ProductAnalysisSkeleton';
import RegulatoryTab from './components/RegulatoryTab/RegulatoryTab';
import ManufacturerTab from './components/ManufacturerTab/ManufacturerTab';
import SupplyRiskTab from './components/SupplyRiskTab/SupplyRiskTab';
import './productAnalysis.styles.scss';
import {
  LOEStatus,
  OpportunityTile,
  SparklineData,
  useProductAnalysisLogic,
} from './useProductAnalysis.logic';

const ProductAnalysis: React.FC = () => {
  const { productData, loading, error } = useProductAnalysisLogic();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'overview');
  const [opportunityData, setOpportunityData] = useState(productData?.overview || null);
  const [sparklineData, setSparklineData] = useState(productData?.sparklines || null);
  const prevProductDataRef = useRef(productData);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const previousGfcRef = useRef<string | null>(null);
  const previousTabRef = useRef<string | null>(null);
  // Expose current abort signal for tabs to use
  const currentAbortSignalRef = useRef<AbortSignal | null>(null);

  // Update active tab when URL tab parameter changes
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Update local state when productData changes - MUST be before any conditional returns
  useEffect(() => {
    if (productData && productData !== prevProductDataRef.current) {
      setOpportunityData(productData.overview);
      setSparklineData(productData.sparklines);
      prevProductDataRef.current = productData;
    }
  }, [productData]);

  // Tab-based lazy loading with abort controllers
  useEffect(() => {
    const gfc = searchParams.get('gfc');

    // Reset abort controllers if GFC changes (new product)
    if (gfc !== previousGfcRef.current && previousGfcRef.current !== null) {
      // Abort all existing requests
      abortControllersRef.current.forEach(controller => {
        controller.abort();
      });
      abortControllersRef.current.clear();
      previousTabRef.current = null; // Reset tab tracking when GFC changes
      productAnalysisStore.clearNadWaiversData();
      productAnalysisStore.clearLabelerInformationData();
    }
    previousGfcRef.current = gfc;

    // Don't fetch if no GFC or still loading initial data
    if (!gfc || loading || !productData) return;

    // Abort ALL previous requests when switching tabs (not just the current tab)
    // This ensures that when switching from one tab to another, the previous tab's request is cancelled
    if (previousTabRef.current !== activeTab) {
      abortControllersRef.current.forEach((controller, tabId) => {
        controller.abort();
        abortControllersRef.current.delete(tabId);
      });
      previousTabRef.current = activeTab;
    }

    // Create new abort controller for current tab
    const abortController = new AbortController();
    abortControllersRef.current.set(activeTab, abortController);
    const signal = abortController.signal;
    // Expose current signal for tabs to use
    currentAbortSignalRef.current = signal;

    // Fetch data based on active tab
    const fetchTabData = async () => {
      try {
        switch (activeTab) {
          case 'overview':
            // Fetch all opportunity data using split endpoints in parallel
            // This provides better UX with individual section loaders
            // Note: Initial data is fetched by useProductAnalysis.logic hook on mount
            // This is only for cases where we need to re-fetch (e.g., tab switching)
            if (!productAnalysisStore.opportunityOverview || !productAnalysisStore.sparklineData) {
              await productAnalysisStore.fetchAllOpportunityData(
                {
                  gfc: gfc.trim(),
                  period: '3 months',
                },
                signal
              );
            }
            // Note: Market landscape data is fetched by useMarketLandscapeTab hook in MarketLandscapeSection
            // Note: COT distribution is included in fetchAllOpportunityData
            break;

          case 'pricing':
            // Only fetch if not already loaded
            if (!productAnalysisStore.pricingData) {
              await productAnalysisStore.fetchPricingData(
                {
                  gfc: gfc.trim(),
                },
                signal
              );
            }
            break;

          case 'competitors':
            // Only fetch if not already loaded
            if (!productAnalysisStore.competitorsData) {
              await productAnalysisStore.fetchCompetitorsData(
                {
                  gfc: gfc.trim(),
                  page: 1,
                  page_size: 10,
                },
                signal
              );
            }
            break;

          case 'manufacturer': {
            const manufacturerPromises: Promise<void>[] = [];
            if (!productAnalysisStore.nadWaiversData) {
              manufacturerPromises.push(
                productAnalysisStore.fetchNadWaivers(
                  { gfc: gfc.trim(), page: 1, page_size: 10 },
                  signal
                )
              );
            }
            if (!productAnalysisStore.labelerInformationData) {
              manufacturerPromises.push(
                productAnalysisStore.fetchLabelerInformation(
                  { gfc: gfc.trim(), page: 1, page_size: 10 },
                  signal
                )
              );
            }
            if (manufacturerPromises.length > 0) {
              await Promise.all(manufacturerPromises);
            }
            break;
          }

          case 'import-export':
            // Import & Export tab (formerly regulatory)
            // Only fetch if not already loaded
            // if (!productAnalysisStore.regulatoryData) {
            //   await productAnalysisStore.fetchRegulatoryData(
            //     {
            //       gfc: gfc.trim(),
            //     },
            //     signal
            //   );
            // }
            break;

          // Other tabs (supply-risk) handle their own data fetching
          default:
            break;
        }
      } catch (err) {
        // Ignore aborted errors
        if (err instanceof Error && err.name === 'CanceledError') {
          return;
        }
      }
    };

    fetchTabData();

    // Cleanup: abort request if component unmounts or tab changes
    return () => {
      const currentController = abortControllersRef.current.get(activeTab);
      if (currentController && currentController === abortController) {
        currentController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        abortControllersRef.current.delete(activeTab);
      }
    };
  }, [activeTab, loading, productData, searchParams]);

  // Cleanup all abort controllers on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach(controller => {
        controller.abort();
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      abortControllersRef.current.clear();
    };
  }, []);

  // Calculate wrapper height based on scaled content to prevent extra white space
  // Also ensures the wrapper fills at least the viewport height (important for 4K screens)
  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current || loading || !productData) return;

    const updateWrapperHeight = () => {
      // Use requestAnimationFrame to ensure calculation happens after render
      requestAnimationFrame(() => {
        if (!contentRef.current || !wrapperRef.current) return;

        const contentHeight = contentRef.current.scrollHeight || 0;
        // Since content is scaled to 65%, the visual height is 65% of the scrollHeight
        const scaledHeight = contentHeight * 0.65;
        // Ensure wrapper fills at least the viewport height (for 4K and large screens)
        const viewportHeight = window.innerHeight;
        const finalHeight = Math.max(scaledHeight, viewportHeight);
        wrapperRef.current.style.minHeight = `${finalHeight}px`;
      });
    };

    // Initial calculation with a small delay to ensure content is rendered
    const timeoutId = setTimeout(updateWrapperHeight, 100);

    // Update on window resize
    window.addEventListener('resize', updateWrapperHeight);

    // Use ResizeObserver to watch for content changes
    const resizeObserver = new ResizeObserver(() => {
      updateWrapperHeight();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWrapperHeight);
      resizeObserver.disconnect();
    };
  }, [loading, productData, activeTab]); // Include activeTab to recalculate when tab changes

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Update URL with tab parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (tabId === 'overview') {
      // Remove tab param for overview (default)
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tabId);
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleOpportunityDataUpdate = useCallback(
    (tiles: OpportunityTile[], loeStatus: LOEStatus, sparklines: SparklineData) => {
      // Only update if data actually changed
      const currentData = { tiles, loeStatus };
      const dataChanged =
        JSON.stringify(currentData) !== JSON.stringify(opportunityData) ||
        JSON.stringify(sparklines) !== JSON.stringify(sparklineData);

      if (dataChanged) {
        setOpportunityData({ tiles, loeStatus });
        setSparklineData(sparklines);

        // Also update productData if it exists
        if (productData) {
          productData.overview = { tiles, loeStatus };
          productData.sparklines = sparklines;
        }
      }
    },
    [opportunityData, sparklineData, productData]
  );

  if (loading) {
    return <ProductAnalysisSkeleton />;
  }

  if (error) {
    return (
      <div className="product-analysis-page-wrapper" ref={wrapperRef}>
        <div className="product-analysis-page" ref={contentRef}>
          <div className="error-container">
            <h3>Error loading product analysis</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!productData) {
    return (
      <div className="product-analysis-page-wrapper" ref={wrapperRef}>
        <div className="product-analysis-page" ref={contentRef}>
          <div className="no-data-container">
            <h3>No product data available</h3>
            <p>Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="product-analysis-page-wrapper" ref={wrapperRef}>
        <div className="product-analysis-page" ref={contentRef}>
          <BreadcrumbSection breadcrumbData={productData.breadcrumb} />
          <PageTabs activeTab={activeTab} onTabChange={handleTabChange} />

          <div className="container">
            {activeTab === 'overview' && opportunityData && sparklineData && (
              <OverviewTab
                opportunityData={opportunityData}
                sparklineData={sparklineData}
                onOpportunityDataUpdate={handleOpportunityDataUpdate}
              />
            )}

            {activeTab === 'pricing' && <PricingTab />}

            {activeTab === 'competitors' && (
              <CompetitorsTab
                competitorData={
                  productAnalysisStore.competitorsData?.competitors || productData.competitors
                }
                andaData={productAnalysisStore.competitorsData?.anda || productData.anda}
              />
            )}

            {activeTab === 'manufacturer' && <ManufacturerTab />}

            {activeTab === 'import-export' && (
              <RegulatoryTab
                regulatoryData={productAnalysisStore.regulatoryData || productData.regulatory}
                complianceData={productData.compliance}
              />
            )}

            {activeTab === 'supply-risk' && <SupplyRiskTab />}
          </div>

          {/* {productData && <DataFooter dataSources={productData.dataSources} />} */}
        </div>
      </div>
      {/* ActionPanel moved outside the transformed container so position: fixed works correctly */}
      <ActionPanel />
    </>
  );
};

export default ProductAnalysis;
